export const API_BASE = import.meta.env.VITE_API_BASE || "";

const TOKEN_KEY = "dp-agenten-token";
const USER_KEY = "dp-agenten-user";

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  role: "admin" | "agent";
  // Optional, gefüllt wenn role=="agent":
  agent_code?: string | null;
  agent_type?: "light" | "full" | null;
  agent_status?: "pending" | "active" | "suspended" | "terminated" | null;
};

export function getApiToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setApiToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function setStoredUser(user: AuthUser | null): void {
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  else localStorage.removeItem(USER_KEY);
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init?.headers as Record<string, string>) || {}),
  };
  const token = getApiToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    // Cookies vom Parent-Domain .dpconnect.de (z.B. dp_b2b für B2B-Login-Status)
    // werden cross-origin nur gesendet wenn credentials='include'. Backend-CORS
    // erlaubt das schon (allow_credentials=true).
    credentials: "include",
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body && typeof body.detail === "string") detail = body.detail;
    } catch {
      // ignore non-JSON error bodies
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

export type HealthResponse = {
  status: "ok" | "degraded";
  db: boolean;
  build_version: string;
  now: string;
};

type LoginResponse = {
  token: string;
  user: AuthUser;
};

export async function login(email: string, password: string): Promise<AuthUser> {
  const res = await api.post<LoginResponse>("/auth/login", { email, password });
  setApiToken(res.token);
  setStoredUser(res.user);
  return res.user;
}

export async function fetchMe(): Promise<AuthUser> {
  return api.get<AuthUser>("/auth/me");
}

export function logout(): void {
  setApiToken(null);
  setStoredUser(null);
}
